import { useState, useEffect, useCallback, useRef } from 'react';
import { ApiState, ApiError, ApiResponse, ApiStateError } from './types';
import { queryCache } from './cache';
import { Milliseconds } from 'types/units';
import { calculateBackoffDelay } from '../../utils/retryBackoff';
import { isRetryableStatus } from '../../constants/httpStatus';

interface QueryOptions<T> {
  enabled?: boolean;
  cacheTime?: Milliseconds;
  retry?: number; // Max retry attempts on a retryable error (default 0 = no retry)
  retryDelay?: Milliseconds; // Base delay for exponential backoff (default 1000ms)
  onSuccess?: (data: T[]) => void; // TODO: Decide if we need the onSuccess and onError callbacks if we are using ApiState to communicate status and data
  onError?: (error: ApiError) => void;
}

/**
 * Retryable per the same rules as ApiClient.executeWithRetry — network
 * errors (`NETWORK_ERROR`, set by client.ts's response interceptor) or a
 * retryable HTTP status. AbortErrors are handled separately by the caller.
 */
function isRetryableQueryError(error: ApiError): boolean {
  if (error.code === 'NETWORK_ERROR') {
    return true;
  }
  return error.response?.status
    ? isRetryableStatus(error.response.status)
    : false;
}

interface MutationOptions<TData, TVariables> {
  onSuccess?: (data: TData, variables: TVariables) => void; // TODO: Decide if we need the onSuccess, onError, and onSettled callbacks if we are using ApiState to communicate status and data
  onError?: (error: ApiError, variables: TVariables) => void;
  onSettled?: (
    data: TData | undefined,
    error: ApiError | null,
    variables: TVariables
  ) => void;
}

export function useQuery<T>(
  key: string,
  fetcher: (signal?: AbortSignal) => Promise<ApiResponse<T>>, // TODO: Change the name from fetcher to avoid confusion with fetch API (apiCall), // TODO: Create formalized return type
  options: QueryOptions<T> = {}
) {
  // TODO: Create Return Type
  const [state, setState] = useState<ApiState<T>>({ status: 'idle' });

  // Destructure options to avoid infinite re-renders
  const {
    enabled = true,
    cacheTime,
    onSuccess,
    onError,
    retry = 0,
    retryDelay = 1000,
  } = options;

  // Track in-flight query so new executions can cancel old ones.
  // This enforces latest-query-wins behavior for typeahead/search workloads.
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestVersionRef = useRef(0);

  const isAbortError = (error: unknown): boolean => {
    return (
      (error instanceof DOMException && error.name === 'AbortError') ||
      (typeof error === 'object' &&
        error !== null &&
        'name' in error &&
        String((error as { name?: string }).name) === 'AbortError') ||
      (typeof error === 'object' &&
        error !== null &&
        'code' in error &&
        String((error as { code?: string }).code) === 'ERR_CANCELED')
    );
  };

  const execute = useCallback(async () => {
    // TODO: Re-enable caching. Updating a grade name does not allow product grade to be updated because it's cache is not invalidated
    // const cached = queryCache.get<T>(key);
    // if (cached) {
    //   setState({ status: 'success', data: cached });
    //   return;
    // }

    // Cancel previous in-flight request before starting a new one.
    abortControllerRef.current?.abort();

    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    const requestVersion = ++requestVersionRef.current;

    setState({ status: 'loading' });

    const isStale = () =>
      abortController.signal.aborted ||
      requestVersion !== requestVersionRef.current;

    let attempt = 0;
    for (;;) {
      try {
        const response = await fetcher(abortController.signal);

        // Ignore stale or aborted responses.
        if (isStale()) {
          return;
        }

        queryCache.set(key, response.data, cacheTime);
        setState({ status: 'success', data: response.data });
        onSuccess?.([response.data]);
        break;
      } catch (err) {
        if (isAbortError(err) || isStale()) {
          return;
        }

        const error = err as ApiError;

        if (attempt < retry && isRetryableQueryError(error)) {
          const delay = calculateBackoffDelay(attempt, retryDelay);
          attempt += 1;
          // Abort-aware wait: a cancelled/superseded query resolves the
          // backoff sleep immediately instead of sitting through the full
          // delay before the isStale() check below can catch it.
          await new Promise<void>(resolve => {
            const timer = setTimeout(resolve, delay);
            abortController.signal.addEventListener(
              'abort',
              () => {
                clearTimeout(timer);
                resolve();
              },
              { once: true }
            );
          });
          if (isStale()) {
            return;
          }
          continue;
        }

        const apiError: ApiStateError = {
          status: 'error',
          error: {
            message: error.message,
            code: error.code,
            details: error.response?.data,
          },
        };
        setState(apiError);
        onError?.(error);
        break;
      }
    }

    // Only clear the ref once the attempt/retry sequence has fully settled —
    // clearing it inside the retry loop would let a stale ref survive a
    // concurrent execute() call's cancellation, since it checks
    // abortControllerRef.current?.abort() before starting.
    if (abortControllerRef.current === abortController) {
      abortControllerRef.current = null;
    }
  }, [key, fetcher, cacheTime, onSuccess, onError, retry, retryDelay]);

  useEffect(() => {
    if (enabled) {
      execute();
    }

    return () => {
      abortControllerRef.current?.abort();
    };
  }, [execute, enabled]);

  return {
    ...state,
    refetch: execute,
    invalidate: () => queryCache.invalidate(key),
  };
}

export function useMutation<TData = unknown, TVariables = unknown>(
  mutationFn: (variables: TVariables) => Promise<ApiResponse<TData>>,
  options: MutationOptions<TData, TVariables> = {}
) {
  const [state, setState] = useState<ApiState<TData>>({ status: 'idle' });

  const mutate = async (variables: TVariables): Promise<TData> => {
    setState({ status: 'loading' });

    try {
      const response = await mutationFn(variables);
      setState({ status: 'success', data: response.data });
      options.onSuccess?.(response.data, variables);
      options.onSettled?.(response.data, null, variables);
      return response.data;
    } catch (err) {
      const error = err as ApiError;
      setState({
        status: 'error',
        error: {
          message: error.message,
          code: error.code,
          details: error.response?.data,
        },
      });
      options.onError?.(error, variables);
      options.onSettled?.(undefined, error, variables);
      throw error;
    }
  };

  return {
    ...state,
    mutate,
    reset: () => setState({ status: 'idle' }),
  };
}

interface InfiniteQueryOptions<T> extends QueryOptions<T> {
  getNextPageParam: (lastPage: T, pages: T[]) => unknown;
}

export function useInfiniteQuery<T>(
  key: string,
  fetcher: (pageParam: unknown) => Promise<ApiResponse<T>>,
  options: InfiniteQueryOptions<T>
) {
  const [pages, setPages] = useState<T[]>([]);
  const [state, setState] = useState<ApiState<T[]>>({ status: 'idle' });

  // TODO: Implement caching for infinite queries

  const { onSuccess, onError, getNextPageParam } = options;

  const fetchNextPage = useCallback(async () => {
    if (state.status === 'loading') return;

    setState({ status: 'loading' });

    try {
      const pageParam = pages.length
        ? getNextPageParam(pages[pages.length - 1], pages)
        : undefined;
      const response = await fetcher(pageParam);
      const newPages = [...pages, response.data];
      setPages(newPages);
      setState({ status: 'success', data: newPages });
      onSuccess?.(newPages);
    } catch (err) {
      const error = err as ApiError;
      setState({
        status: 'error',
        error: {
          message: error.message,
          code: error.code,
          details: error.response?.data,
        },
      });
      onError?.(error);
    }
  }, [state.status, pages, fetcher, onSuccess, onError, getNextPageParam]);

  useEffect(() => {
    if (options.enabled !== false) {
      fetchNextPage();
    }
  }, [options.enabled, fetchNextPage]);

  return {
    ...state,
    pages,
    fetchNextPage,
    hasNextPage:
      pages.length > 0 &&
      options.getNextPageParam(pages[pages.length - 1], pages) !== undefined,
    reset: () => {
      setPages([]);
      setState({ status: 'idle' });
    },
  };
}
